<template><div><h1 id="adressimport-mit-manueller-zuordnung" tabindex="-1"><a class="header-anchor" href="#adressimport-mit-manueller-zuordnung"><span>Adressimport mit manueller Zuordnung</span></a></h1>
<p>Adressimport mit manueller Zuordnung — Import von Adressen mit manueller Nachbearbeitung</p>
<h2 id="beschreibung" tabindex="-1"><a class="header-anchor" href="#beschreibung"><span>Beschreibung</span></a></h2>
<p>Der Adressimport mit manueller Zuordnung verarbeitet alle Datensätze automatisch, die ohne Abweichung einer vorbestehnden Person in funtrade zugeordnet werden können. Die verbleibenden Datensätze werden manuell neu erfasst oder einer bestehenden Person zugeordnet. Es gibt Adressimport-Positionen die ausschliesslich mit diesem Import funktioneren (siehe dazu <RouteLink to="/referenz/refs/ft.refdata.adrimppos.html" title="Adressimport in Datenbank (Positionen)">Adressimport-Positionen</RouteLink>).</p>
<p>Der Grundsätzliche Ablauf ist wie folgt:</p>
<ol>
<li>
<p>Import erfassen</p>
</li>
<li>
<p><RouteLink to="/referenz/refs/ft.refdata.adrimppos.html" title="Adressimport in Datenbank (Positionen)">Adressimport-Positionen</RouteLink> definieren</p>
</li>
<li>
<p>Daten einlesen</p>
</li>
<li>
<p>Adressen/Personen manuell erfassen und zuordnen</p>
</li>
<li>
<p>Daten verabeiten</p>
</li>
</ol>
<p>Pfad: <code v-pre>Datenaustausch → Adressimport mit manueller Zuordnung</code></p>
<h2 id="datenfelder" tabindex="-1"><a class="header-anchor" href="#datenfelder"><span>Datenfelder</span></a></h2>
<dl>
<dt>Job</dt>
<dd>Datum des Importjobs</dd>
<dt>ohne Label</dt>
<dd>Laufnummer des Importjobs</dd>
<dt>Beschreibung</dt>
<dd>Beschreibung des Importjobs</dd>
<dt>Quelle</dt>
<dd>Quelleneintrag aus <a class="link" href="ft.refdata.tblquelle.html" title="Quellen">Quelle Stammtabelle</a></dd>
<dt>Quelldatei</dt>
<dd>Feld für Upload der Quelldatei</dd>
<dt>Format</dt>
<dd>Format der einzulesenden Datei.
<table>
<thead>
<tr>
<th>Hinweise</th>
</tr>
</thead>
<tbody>
<tr>
<td>getrennt mit ;</td>
</tr>
<tr>
<td>getrennt mit TAB</td>
</tr>
<tr>
<td>feste Feldlänge</td>
</tr>
<tr>
<td>xlsx</td>
</tr>
</tbody>
</table>
</dd>
<dt>verarbeiten postalisch unvollständig</dt>
<dd>Verarbeitung, auch wenn Mindestangaben wie Name oder Ort nicht vorhanden sind. Wichtig, wenn z.B. &quot;E-Mail Only-Adressen&quot; eingelesen werden sollen.</dd>
<dt>Status</dt>
<dd>
<table>
<thead>
<tr>
<th>Status</th>
<th>Beschreibung</th>
</tr>
</thead>
<tbody>
<tr>
<td>erfasst, noch nicht eingelesen</td>
<td>Job ist erfasst, aber das Einlesen der Daten ist noch nicht gestartet worden</td>
</tr>
<tr>
<td>einlesen läuft</td>
<td>Das Einlesen der Daten läuft und ist noch nicht abgeschlossen</td>
</tr>
<tr>
<td>erfasst, teilweise eingelesen</td>
<td>Das Einlesen der Daten wurde abgebrochen</td>
</tr>
<tr>
<td>eingelesen, noch nicht verarbeitet</td>
<td>Die Daten sind eingelesen aber noch keine verarbeitet (es hat noch Daten für die manuelle Verarbeitung)</td>
</tr>
<tr>
<td>teilweise verarbeitet</td>
<td>Die Daten sind eingelesen aber nur zum Teil verarbeitet (es hat noch Daten für die manuelle Verarbeitung)</td>
</tr>
<tr>
<td>verarbeitet</td>
<td>Die Daten sind vollständig eingelesen und verarbeitet</td>
</tr>
</tbody>
</table>
</dd>
</dl>
<h2 id="funktionen" tabindex="-1"><a class="header-anchor" href="#funktionen"><span>Funktionen</span></a></h2>
<h3 id="daten-einlesen" tabindex="-1"><a class="header-anchor" href="#daten-einlesen"><span>Daten einlesen...</span></a></h3>
<p>Startet das Einlesen der Adressdaten.</p>
<h3 id="eingelesene-daten-zurucksetzen" tabindex="-1"><a class="header-anchor" href="#eingelesene-daten-zurucksetzen"><span>eingelesene Daten zurücksetzen...</span></a></h3>
<p>Entfernt die eingelesenen Adressdaten. Zurücksetzen ist nur möglich, wenn noch keine Daten verarbeitet worden sind (Status=&quot;eingelesen, noch nicht verarbeitet&quot;).</p>
<h3 id="daten-verarbeiten" tabindex="-1"><a class="header-anchor" href="#daten-verarbeiten"><span>Daten verarbeiten...</span></a></h3>
<p>Die Daten werden verarbeitet. Insbesondere bedeutet dies:</p>
<ul>
<li>bei allen verarbeitbaren Datensätzen (Status = &quot;OK&quot; oder &quot;zugeordnet&quot;) werden die zusätzlichen Daten, ergänzt (z.B. Merkmale, Ereignisse, Buchungen, je nach Definition in den <RouteLink to="/referenz/refs/ft.refdata.adrimppos.html" title="Adressimport in Datenbank (Positionen)">Import-Positionen</RouteLink>)</li>
<li>dabei werden Adressen mit Status OK neu in funtrade erfasst</li>
<li>Adressen mit Status &quot;abgelehnt&quot; oder &quot;Fehler&quot; werden ignoriert</li>
</ul>
<p>Dieser Job kann so oft neu gestartet werden, bis alle Datensätze abgelehnt oder verarbeitet worden sind.</p>
<h3 id="import-zuordnung-tab" tabindex="-1"><a class="header-anchor" href="#import-zuordnung-tab"><span>Import Zuordnung (Tab)</span></a></h3>
<p>Nach dem Import der Adressdaten können nicht zuordbare Adressen manuell verknüpft werden. Dies geschieht im Tab <RouteLink to="/referenz/refs/ft.refdata.importverarbeiten.html" title="Import Zuordnung (Adressimport verarbeiten)">Import Zuordnung (Adressimport verarbeiten)</RouteLink>.</p>
<h2 id="siehe-auch" tabindex="-1"><a class="header-anchor" href="#siehe-auch"><span>Siehe auch</span></a></h2>
<p><RouteLink to="/referenz/refs/ft.refdata.adrimppos.html" title="Adressimport in Datenbank (Positionen)">Adressimport in Datenbank (Positionen)</RouteLink> , <RouteLink to="/referenz/refs/ft.refdata.importverarbeiten.html" title="Import Zuordnung (Adressimport verarbeiten)">Import Zuordnung (Adressimport verarbeiten)</RouteLink></p>
</div></template>


